import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { AlertCircle, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../../../utils/config';

const REQUEST_TIMEOUT_MS = 20000;

const PurchasedItemsModal = ({ isOpen, onClose, purchasedItem, onSave, mode = 'add' }) => {
  const [categoryType, setCategoryType] = useState('goods'); // 'goods' or 'services'
  const [formData, setFormData] = useState({
    goods_data: {
      item_name: '',
      unit_price: '',
      gst_treatment: 'inclusive of GST',
      gst_rate: '',
      non_taxable: 0,
      quantity: '',
      purchase_order_no: '',
      supplier: '',
      purchase_date: '',
      description: ''
    },
    services_data: {
      service_name: '',
      price: '',
      gst_treatment: 'inclusive of GST',
      gst_rate: '',
      non_taxable: 0,
      supplier: '',
      purchase_date: '',
      description: ''
    }
  });

  const [suppliers, setSuppliers] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(API_ENDPOINTS.SUPPLIERS_LIST, {
          headers: { 'Accept': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
        });
        const data = response.data;
        setSuppliers(Array.isArray(data) ? data : (data.results || []));
      } catch {
        setSuppliers([]);
      }
    };
    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && purchasedItem) {
        const type = purchasedItem.category_type || 'goods';
        setCategoryType(type);
        
        if (type === 'goods') {
          const goodsData = purchasedItem.goods_data || {};
          // Convert DD-MM-YYYY to YYYY-MM-DD for date input
          const purchaseDate = goodsData.purchase_date || '';
          let formattedDate = purchaseDate;
          if (purchaseDate && purchaseDate.includes('-') && purchaseDate.split('-')[0].length === 2) {
            const [day, month, year] = purchaseDate.split('-');
            formattedDate = `${year}-${month}-${day}`;
          }
          setFormData(prev => ({
            ...prev,
            goods_data: {
              item_name: goodsData.item_name || '',
              unit_price: goodsData.unit_price || '',
              gst_treatment: goodsData.gst_treatment || 'inclusive of GST',
              gst_rate: goodsData.gst_rate || '',
              non_taxable: goodsData.non_taxable || 0,
              quantity: goodsData.quantity || '',
              purchase_order_no: goodsData.purchase_order_no || '',
              supplier: goodsData.supplier || '',
              purchase_date: formattedDate,
              description: goodsData.description || ''
            }
          }));
        } else {
          const servicesData = purchasedItem.services_data || {};
          // Convert DD-MM-YYYY to YYYY-MM-DD for date input
          const purchaseDate = servicesData.purchase_date || '';
          let formattedDate = purchaseDate;
          if (purchaseDate && purchaseDate.includes('-') && purchaseDate.split('-')[0].length === 2) {
            const [day, month, year] = purchaseDate.split('-');
            formattedDate = `${year}-${month}-${day}`;
          }
          setFormData(prev => ({
            ...prev,
            services_data: {
              service_name: servicesData.service_name || '',
              price: servicesData.price || '',
              gst_treatment: servicesData.gst_treatment || 'inclusive of GST',
              gst_rate: servicesData.gst_rate || '',
              non_taxable: servicesData.non_taxable || 0,
              supplier: servicesData.supplier || '',
              purchase_date: formattedDate,
              description: servicesData.description || ''
            }
          }));
        }
      } else {
        // Reset form for add mode
        setCategoryType('goods');
        setFormData({
          goods_data: {
            item_name: '',
            unit_price: '',
            gst_treatment: 'inclusive of GST',
            gst_rate: '',
            non_taxable: 0,
            quantity: '',
            purchase_order_no: '',
            supplier: '',
            purchase_date: '',
            description: ''
          },
          services_data: {
            service_name: '',
            price: '',
            gst_treatment: 'inclusive of GST',
            gst_rate: '',
            non_taxable: 0,
            supplier: '',
            purchase_date: '',
            description: ''
          }
        });
      }
      setErrors({});
      setErrorMessage('');
    }
  }, [purchasedItem, mode, isOpen]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Clear error when user starts typing
    if (errors?.[`${section}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const currentData = categoryType === 'goods' ? formData.goods_data : formData.services_data;
    const dataKey = categoryType === 'goods' ? 'goods_data' : 'services_data';

    if (categoryType === 'goods') {
      if (!currentData.item_name?.trim()) {
        newErrors[`${dataKey}.item_name`] = 'Item name is required';
      }
      if (!currentData.unit_price?.trim()) {
        newErrors[`${dataKey}.unit_price`] = 'Unit price is required';
      }
      if (!currentData.quantity?.trim()) {
        newErrors[`${dataKey}.quantity`] = 'Quantity is required';
      }
      if (!currentData.supplier?.trim()) {
        newErrors[`${dataKey}.supplier`] = 'Supplier is required';
      }
      if (!currentData.purchase_date?.trim()) {
        newErrors[`${dataKey}.purchase_date`] = 'Purchase date is required';
      }
    } else {
      if (!currentData.service_name?.trim()) {
        newErrors[`${dataKey}.service_name`] = 'Service name is required';
      }
      if (!currentData.price?.trim()) {
        newErrors[`${dataKey}.price`] = 'Price is required';
      }
      if (!currentData.supplier?.trim()) {
        newErrors[`${dataKey}.supplier`] = 'Supplier is required';
      }
      if (!currentData.purchase_date?.trim()) {
        newErrors[`${dataKey}.purchase_date`] = 'Purchase date is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };
      const axiosConfig = {
        headers,
        timeout: REQUEST_TIMEOUT_MS
      };

      // Convert YYYY-MM-DD to DD-MM-YYYY for API
      const formatDateForAPI = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
          const [year, month, day] = dateStr.split('-');
          return `${day}-${month}-${year}`;
        }
        return dateStr; // Already in DD-MM-YYYY format
      };

      const payload = {
        category_type: categoryType,
        [categoryType === 'goods' ? 'goods_data' : 'services_data']: categoryType === 'goods' 
          ? {
              ...formData.goods_data,
              unit_price: String(formData.goods_data.unit_price),
              quantity: String(formData.goods_data.quantity),
              purchase_date: formatDateForAPI(formData.goods_data.purchase_date),
              non_taxable: Number(formData.goods_data.non_taxable) || 0
            }
          : {
              ...formData.services_data,
              price: String(formData.services_data.price),
              purchase_date: formatDateForAPI(formData.services_data.purchase_date),
              non_taxable: Number(formData.services_data.non_taxable) || 0
            }
      };

      if (mode === 'edit' && purchasedItem?.id) {
        await axios.put(
          API_ENDPOINTS.PURCHASED_ITEMS_UPDATE(purchasedItem.id),
          payload,
          axiosConfig
        );
      } else {
        await axios.post(
          API_ENDPOINTS.PURCHASED_ITEMS_CREATE,
          payload,
          axiosConfig
        );
      }

      onSave();
      onClose();
    } catch (error) {
      const timeoutTriggered = error?.code === 'ECONNABORTED';
      const apiError =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        (timeoutTriggered
          ? 'Request timed out. Please check your network or try again in a moment.'
          : error?.message) ||
        'Failed to save purchased item. Please try again.';
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const currentData = categoryType === 'goods' ? formData.goods_data : formData.services_data;
  const dataKey = categoryType === 'goods' ? 'goods_data' : 'services_data';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-gray-900">
              {mode === 'edit' ? 'Edit' : 'Add'} Purchased Item
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errorMessage && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <p className="text-sm">{errorMessage}</p>
            </div>
          )}

          {/* Category Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="goods"
                  checked={categoryType === 'goods'}
                  onChange={(e) => setCategoryType(e.target.value)}
                  className="mr-2"
                  disabled={mode === 'edit'}
                />
                <span>Goods</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="services"
                  checked={categoryType === 'services'}
                  onChange={(e) => setCategoryType(e.target.value)}
                  className="mr-2"
                  disabled={mode === 'edit'}
                />
                <span>Services</span>
              </label>
            </div>
          </div>

          {/* Goods Fields */}
          {categoryType === 'goods' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Item Name"
                  type="text"
                  placeholder="Enter item name"
                  value={currentData.item_name}
                  onChange={(e) => handleInputChange(dataKey, 'item_name', e.target.value)}
                  error={errors[`${dataKey}.item_name`]}
                  required
                />
                <Input
                  label="Unit Price"
                  type="text"
                  placeholder="Enter unit price"
                  value={currentData.unit_price}
                  onChange={(e) => handleInputChange(dataKey, 'unit_price', e.target.value)}
                  error={errors[`${dataKey}.unit_price`]}
                  required
                />
                <Input
                  label="Quantity"
                  type="text"
                  placeholder="Enter quantity"
                  value={currentData.quantity}
                  onChange={(e) => handleInputChange(dataKey, 'quantity', e.target.value)}
                  error={errors[`${dataKey}.quantity`]}
                  required
                />
                <Input
                  label="Purchase Order No"
                  type="text"
                  placeholder="Enter purchase order number"
                  value={currentData.purchase_order_no}
                  onChange={(e) => handleInputChange(dataKey, 'purchase_order_no', e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currentData.supplier}
                    onChange={(e) => handleInputChange(dataKey, 'supplier', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select supplier</option>
                    {suppliers.map((s) => {
                      const name = s.supplier_data?.supplier_name;
                      return <option key={s.id} value={name}>{name}</option>;
                    })}
                  </select>
                  {errors[`${dataKey}.supplier`] && (
                    <p className="text-xs text-red-500 mt-1">{errors[`${dataKey}.supplier`]}</p>
                  )}
                </div>
                <Input
                  label="Purchase Date"
                  type="date"
                  placeholder="Select purchase date"
                  value={currentData.purchase_date}
                  onChange={(e) => handleInputChange(dataKey, 'purchase_date', e.target.value)}
                  error={errors[`${dataKey}.purchase_date`]}
                  required
                />
                <Input
                  label="GST Treatment"
                  type="text"
                  placeholder="e.g., inclusive of GST"
                  value={currentData.gst_treatment}
                  onChange={(e) => handleInputChange(dataKey, 'gst_treatment', e.target.value)}
                />
                <Input
                  label="GST Rate"
                  type="text"
                  placeholder="e.g., 18%"
                  value={currentData.gst_rate}
                  onChange={(e) => handleInputChange(dataKey, 'gst_rate', e.target.value)}
                />
                <Input
                  label="Non Taxable"
                  type="number"
                  placeholder="0"
                  value={currentData.non_taxable}
                  onChange={(e) => handleInputChange(dataKey, 'non_taxable', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Enter description"
                  value={currentData.description}
                  onChange={(e) => handleInputChange(dataKey, 'description', e.target.value)}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Services Fields */}
          {categoryType === 'services' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Service Name"
                  type="text"
                  placeholder="Enter service name"
                  value={currentData.service_name}
                  onChange={(e) => handleInputChange(dataKey, 'service_name', e.target.value)}
                  error={errors[`${dataKey}.service_name`]}
                  required
                />
                <Input
                  label="Price"
                  type="text"
                  placeholder="Enter price"
                  value={currentData.price}
                  onChange={(e) => handleInputChange(dataKey, 'price', e.target.value)}
                  error={errors[`${dataKey}.price`]}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currentData.supplier}
                    onChange={(e) => handleInputChange(dataKey, 'supplier', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Select supplier</option>
                    {suppliers.map((s) => {
                      const name = s.supplier_data?.supplier_name;
                      return <option key={s.id} value={name}>{name}</option>;
                    })}
                  </select>
                  {errors[`${dataKey}.supplier`] && (
                    <p className="text-xs text-red-500 mt-1">{errors[`${dataKey}.supplier`]}</p>
                  )}
                </div>
                <Input
                  label="Purchase Date"
                  type="date"
                  placeholder="Select purchase date"
                  value={currentData.purchase_date}
                  onChange={(e) => handleInputChange(dataKey, 'purchase_date', e.target.value)}
                  error={errors[`${dataKey}.purchase_date`]}
                  required
                />
                <Input
                  label="GST Treatment"
                  type="text"
                  placeholder="e.g., inclusive of GST"
                  value={currentData.gst_treatment}
                  onChange={(e) => handleInputChange(dataKey, 'gst_treatment', e.target.value)}
                />
                <Input
                  label="GST Rate"
                  type="text"
                  placeholder="e.g., 5%"
                  value={currentData.gst_rate}
                  onChange={(e) => handleInputChange(dataKey, 'gst_rate', e.target.value)}
                />
                <Input
                  label="Non Taxable"
                  type="number"
                  placeholder="0"
                  value={currentData.non_taxable}
                  onChange={(e) => handleInputChange(dataKey, 'non_taxable', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  placeholder="Enter description"
                  value={currentData.description}
                  onChange={(e) => handleInputChange(dataKey, 'description', e.target.value)}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchasedItemsModal;

